export interface ToolCallText {
    toolName: string;
    /** The call's parsed arguments — a JSON-serializable object matching the tool's schema. */
    input: unknown;
}

export interface ToolResultText {
    toolName: string;
    /** Result already serialized to a string (JSON for objects, raw for plain text). */
    value: string;
    isError?: boolean;
}

/**
 * Serializes tool calls into the same pseudo-XML `<function_calls>` block the model itself emits
 * when the gateway does text-emulated tool calling — the OUTBOUND half of the text tool
 * protocol. Sent in place of native assistant tool-call message parts, because the backend proxy
 * forwards messages to Anthropic verbatim and Anthropic's Messages API rejects anything but
 * user/assistant roles (observed live: `Unexpected role "tool"`). Using the exact format the
 * model already writes keeps its own history consistent with its output.
 */
export function formatFunctionCallsBlock(calls: ToolCallText[]): string {
    const invokes = calls.map((call) => {
        const input = call.input;
        const entries =
            input != null && typeof input === 'object' && !Array.isArray(input)
                ? Object.entries(input as Record<string, unknown>)
                : input != null
                  ? [['input', input] as [string, unknown]]
                  : [];

        const parameters = entries
            .map(([name, value]) => {
                const serialized = typeof value === 'string' ? value : JSON.stringify(value);
                return `<parameter name="${name}">${serialized}</parameter>`;
            })
            .join('\n');

        return parameters
            ? `<invoke name="${call.toolName}">\n${parameters}\n</invoke>`
            : `<invoke name="${call.toolName}">\n</invoke>`;
    });

    return `<function_calls>\n${invokes.join('\n')}\n</function_calls>`;
}

/**
 * Serializes tool results into a `<function_results>` block — the model reads it as the
 * environment reporting what its calls actually did (real created ids included). Sent as a USER
 * text message in place of the native `role: "tool"` message the wire can't carry.
 */
export function formatFunctionResultsBlock(results: ToolResultText[]): string {
    const lines = results.map(
        (result) => `${result.toolName}: ${result.isError ? `ERROR: ${result.value}` : result.value}`
    );

    return `<function_results>\n${lines.join('\n')}\n</function_results>`;
}
